import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { CreateUserParams } from '../admin.users.api';

interface CreateUserDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (userData: CreateUserParams) => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose, onSubmit }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

    const handleSubmit = () => {
        const errors: { [key: string]: string } = {};

        if (!firstName || firstName.trim().length < 1) {
            errors.firstName = 'First name is required';
        }

        if (!lastName || lastName.trim().length < 1) {
            errors.lastName = 'Last name is required';
        }

        if (!userName || userName.trim().length < 5) {
            errors.userName = 'Username must be at least 5 characters';
        } else if (!/^[a-zA-Z0-9_]+$/.test(userName)) {
            errors.userName = 'Username can only contain letters, numbers, and underscores';
        }

        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = 'Valid email is required';
        }

        if (phone && !/^\+?[\d\s\-()]+$/.test(phone)) {
            errors.phone = 'Invalid phone number format';
        }

        if (!password || password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        }

        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const userData: CreateUserParams = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            user_name: userName.trim(),
            email: email.trim(),
            phone: phone.trim() || undefined,
            password: password,
        };

        onSubmit(userData);
        handleClose();
    };

    const handleClose = () => {
        setFirstName('');
        setLastName('');
        setUserName('');
        setEmail('');
        setPhone('');
        setPassword('');
        setShowPassword(false);
        setFormErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create User</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Create a new user on behalf of someone else. The user will be created
                        without email verification. All fields marked with * are required.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="First Name *"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            error={!!formErrors.firstName}
                            helperText={formErrors.firstName}
                            fullWidth
                            size="small"
                        />

                        <TextField
                            label="Last Name *"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            error={!!formErrors.lastName}
                            helperText={formErrors.lastName}
                            fullWidth
                            size="small"
                        />
                    </Box>

                    <TextField
                        label="Username *"
                        value={userName}
                        onChange={(e) => {
                            // Only allow alphanumeric and underscore
                            const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                            setUserName(value);
                        }}
                        error={!!formErrors.userName}
                        helperText={
                            formErrors.userName ||
                            'Minimum 5 characters, only letters, numbers, and underscores'
                        }
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Email *"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!formErrors.email}
                        helperText={formErrors.email || 'User will NOT receive verification email'}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Phone (Optional)"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        error={!!formErrors.phone}
                        helperText={formErrors.phone || 'Format: +1234567890 or (123) 456-7890'}
                        fullWidth
                        size="small"
                        placeholder="+1 (555) 123-4567"
                    />

                    <TextField
                        label="Password *"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!formErrors.password}
                        helperText={formErrors.password || 'Minimum 8 characters'}
                        fullWidth
                        size="small"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    Create User
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateUserDialog;
